import React from 'react';
import { ArrowRight, CheckCircle2, Quote } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useLang } from '../context/LanguageContext';
import useReveal from '../hooks/useReveal';
import CTABand from '../components/CTABand';
import { IMAGES, PILLARS } from '../data/content';

function PageHeader({ eyebrow, title, image }) {
  const { lang } = useLang();
  const cn = lang === 'zh' ? 'font-cn' : '';
  return (
    <section className="relative pt-40 pb-24 overflow-hidden">
      <div className="absolute inset-0">
        <img src={image} alt="" className="w-full h-full object-cover" />
        <div className="absolute inset-0 hero-overlay" />
        <div className="absolute inset-0 grain" />
      </div>
      <div className="relative max-w-7xl mx-auto px-5 lg:px-8">
        <p className={`text-[#C6A15B] tracking-[0.3em] uppercase text-xs mb-4 animate-fadeUp ${cn}`}>{eyebrow}</p>
        <h1 className={`font-serif-display text-4xl md:text-5xl font-semibold text-white animate-fadeUp text-balance ${cn}`} style={{ animationDelay: '0.15s' }}>{title}</h1>
        <div className="gold-rule mt-6 animate-fadeUp" style={{ animationDelay: '0.3s' }} />
      </div>
    </section>
  );
}

export default function About() {
  const { t, lang } = useLang();
  const cn = lang === 'zh' ? 'font-cn' : '';
  useReveal([lang]);

  return (
    <div>
      <PageHeader eyebrow={t('about.eyebrow')} title={t('about.title')} image={IMAGES.aboutHero} />

      <section className="py-24 bg-[#FAF8F3]">
        <div className="max-w-7xl mx-auto px-5 lg:px-8 grid lg:grid-cols-2 gap-14 items-center">
          <div className="reveal reveal-left">
            <p className={`text-[#5c6470] leading-relaxed mb-5 text-lg ${cn}`}>{t('about.p1')}</p>
            <p className={`text-[#5c6470] leading-relaxed mb-5 ${cn}`}>{t('about.pageIntro')}</p>
            <p className={`text-[#5c6470] leading-relaxed mb-5 ${cn}`}>{t('about.p2')}</p>
            <p className={`text-[#5c6470] leading-relaxed ${cn}`}>{t('about.p3')}</p>
          </div>
          <div className="reveal reveal-right relative">
            <div className="absolute -bottom-5 -right-5 w-full h-full border-2 border-[#C8142E]/45 rounded-sm hidden md:block" />
            <img src={IMAGES.consultation} alt="" className="relative rounded-sm shadow-2xl w-full h-[480px] object-cover" />
          </div>
        </div>
      </section>

      {/* Quote */}
      <section className="navy-gradient py-20 relative overflow-hidden">
        <div className="absolute inset-0 grain" />
        <div className="max-w-4xl mx-auto px-5 lg:px-8 text-center relative reveal">
          <Quote className="w-12 h-12 text-[#C6A15B] mx-auto mb-6" />
          <p className={`font-serif-display text-2xl md:text-3xl italic text-white leading-relaxed mb-6 text-balance ${cn}`}>{t('about.quote')}</p>
          <p className={`text-[#C6A15B] text-sm ${cn}`}>{t('about.quoteAuthor')}</p>
        </div>
      </section>

      {/* Values */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-5 lg:px-8">
          <div className="text-center mb-16 reveal">
            <p className={`text-[#A9853E] tracking-[0.3em] uppercase text-xs mb-2 ${cn}`}>{t('pillars.eyebrow')}</p>
            <h2 className={`font-serif-display text-3xl md:text-4xl font-semibold text-[#0B1F3A] ${cn}`}>{t('pillars.title')}</h2>
            <div className="gold-rule mx-auto mt-6" />
          </div>
          <div className="grid md:grid-cols-2 gap-x-12 gap-y-8 max-w-4xl mx-auto">
            {PILLARS.map((p, i) => (
              <div key={i} className="reveal flex items-start gap-4" data-delay={(i % 2) * 90}>
                <CheckCircle2 className="w-6 h-6 text-[#C8142E] shrink-0 mt-0.5" />
                <div>
                  <h3 className={`font-serif-display text-lg font-semibold text-[#0B1F3A] mb-1 ${cn}`}>{p[lang].title}</h3>
                  <p className={`text-[#5c6470] text-sm leading-relaxed ${cn}`}>{p[lang].text}</p>
                </div>
              </div>
            ))}
          </div>
          <div className="text-center mt-14 reveal">
            <Link to="/servicios" className={`inline-flex items-center gap-2 gold-btn rounded-full px-7 py-4 font-semibold ${cn}`}>
              {t('services.all')} <ArrowRight className="w-5 h-5" />
            </Link>
          </div>
        </div>
      </section>

      <CTABand />
    </div>
  );
}
